import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Pill, type PillTone } from "../components/Pill";
import { ApiError } from "../lib/api";
import {
  applyCrmAdvisorSignal,
  crmAdvisorSignalsQueryOptions,
  crmProfileQueryOptions,
  triggerCrmRescan,
} from "./queries";
import type {
  CrmAdvisorSignal,
  CrmAdvisorSignalStatus,
  CrmProfile,
} from "./types";

const SIGNAL_STATUS_TONE: Record<CrmAdvisorSignalStatus, PillTone> = {
  open: "warning",
  applied: "success",
  declined: "neutral",
};

export function CrmProfilesPage() {
  const { data, isLoading, error, refetch } = useQuery(
    crmAdvisorSignalsQueryOptions,
  );

  const deployments = useMemo(() => {
    const slugs = new Set<string>();
    for (const s of data?.advisor_signals ?? []) slugs.add(s.deployment_slug);
    return [...slugs].sort();
  }, [data]);

  return (
    <div className="page">
      <h1>CRM adapter profiles</h1>
      <p className="subtitle">
        Review and apply <code>crm_adapter_proposal</code> signals, inspect the
        active profile per deployment, and trigger a rescan when the schema
        looks stale. Apply opens a pull request — it never mutates the
        runtime adapter directly (FR-019). The reviewer who applied a signal
        cannot also approve its PR (separation of duty).
      </p>

      <details className="panel">
        <summary>How to activate a CRM profile</summary>
        <ol className="step-list">
          <li>
            <strong>Trigger discovery.</strong> Use the{" "}
            <em>Trigger discovery</em> form below to start a rescan for
            any deployment slug, click <em>Rescan</em> inside an existing
            deployment section, wait for the schedule, or let the
            runtime adapter raise <code>crm_schema_drift</code> after a
            fingerprint mismatch.
          </li>
          <li>
            <strong>Wait for the AI advisor.</strong> The worker calls
            the schema advisor over MCP and emits a{" "}
            <code>crm_adapter_proposal</code> with proposed mapping,
            transcript hash, and per-field confidence. The signal lands
            in the table below with status <code>open</code>.
          </li>
          <li>
            <strong>Click Apply on the signal.</strong> This writes the
            profile YAML to a branch at{" "}
            <code>
              packages/connectors/crm_profiles/&lt;deployment&gt;/profile.yml
            </code>{" "}
            and opens a pull request via the GitHub adapter. The runtime
            is unchanged at this point.
          </li>
          <li>
            <strong>A different reviewer merges the PR.</strong>{" "}
            Separation of duty is enforced (FR-019): the reviewer who
            applied the signal is not eligible to approve. CI also
            checks the F1 provenance invariant — the YAML must carry
            either <code>originating_signal_id</code> +{" "}
            <code>mcp_transcript_hash</code> (AI-discovered) or{" "}
            <code>hand_authored_by</code> (hand-authored), never both
            and never neither.
          </li>
          <li>
            <strong>Runtime picks the profile up.</strong> On the next
            adapter boot or schedule tick, <code>profile_loader</code>{" "}
            reads the committed YAML, validates the schema fingerprint,
            and the active profile appears under{" "}
            <em>Active profile</em> below.
          </li>
        </ol>
        <p className="subtle">
          <strong>Hand-authored fallback:</strong> if the customer CRM
          exposes neither MCP nor a database-over-MCP server, hand-write
          the YAML and open a PR directly — skip steps 1-3 and start at
          step 4. The CI guardrail at{" "}
          <code>scripts/ci/check_crm_profile_invariants.py</code>{" "}
          enforces the F1 provenance rule on every commit.
        </p>
      </details>

      <details className="panel">
        <summary>Onboard a brand-new deployment</summary>
        <p>
          A deployment must be registered in the codebase before the{" "}
          <em>Trigger discovery</em> form below can rescan it. For a
          customer install we&apos;ve never seen, the first profile
          lands as a hand-authored YAML via PR. The AI advisor takes
          over for refinement on later rescans.
        </p>
        <ol className="step-list">
          <li>
            <strong>Pick a deployment slug.</strong> Lowercase, hyphens,
            unique across customers — e.g. <code>acme-prod-odoo</code>{" "}
            or <code>contoso-dynamics</code>. This becomes the URL
            segment used everywhere (rescan endpoint, profile path, log
            lines).
          </li>
          <li>
            <strong>Capture the schema fingerprint.</strong> Run a
            one-shot discovery against the live CRM (
            <code>OdooBacking.discover()</code> or the equivalent
            backing&apos;s discover method) and copy the{" "}
            <code>schema_fingerprint</code> string from its output. If
            the customer CRM exposes no MCP and no DB-over-MCP, skip
            this step — leave the fingerprint as the operator&apos;s
            best snapshot of the queried column types.
          </li>
          <li>
            <strong>Author the profile YAML</strong> at{" "}
            <code>
              packages/connectors/crm_profiles/&lt;slug&gt;/profile.yml
            </code>
            . Required keys (F1 invariant):
            <pre className="code-block">{`deployment_slug: acme-prod-odoo
originating_signal_id: null      # AI provenance: null for hand-authored
mcp_transcript_hash: null        # AI provenance: null for hand-authored
hand_authored_by: <your-actor-id>
schema_fingerprint: sha256:<paste-from-discover>

field_mappings:
  - target_code: canonical_reference
    crm_table: project.project
    crm_column: name
    confidence: 1.0
  - target_code: lifecycle_stage_code
    crm_table: project.project
    crm_column: stage_id
    confidence: 1.0
  # ... add a row per canonical field your runtime adapter projects

lifecycle_stage_mapping:
  "Draft":       po_draft
  "In Progress": po_execution
  "Done":        po_closed
  # raw CRM labels → ADR-012 stage codes; missing rows raise
  # crm_unknown_stage_value rather than coerce silently.`}</pre>
            See{" "}
            <code>
              packages/connectors/crm_profiles/placet-pilot-odoo/profile.yml
            </code>{" "}
            for a working reference.
          </li>
          <li>
            <strong>Open a PR.</strong> CI runs{" "}
            <code>scripts/ci/check_crm_profile_invariants.py</code>{" "}
            which fails if F1 is violated — i.e., all three of{" "}
            <code>originating_signal_id</code>,{" "}
            <code>mcp_transcript_hash</code>, and{" "}
            <code>hand_authored_by</code> are simultaneously null, or
            any of the three keys is missing entirely.
          </li>
          <li>
            <strong>A different reviewer merges.</strong>{" "}
            Separation-of-duty (FR-019): the reviewer who authors a
            CRM-profile PR is not eligible to approve it.
          </li>
          <li>
            <strong>After merge, the deployment is registered.</strong>{" "}
            On next worker boot or schedule tick,{" "}
            <code>profile_loader</code> reads the YAML, validates the
            schema fingerprint against the live MCP/DB, and surfaces
            the deployment under <em>Active profiles</em>. The{" "}
            <em>Trigger discovery</em> form below now works for this
            slug — useful when you want the AI advisor to refine the
            initial mapping or adapt to schema drift.
          </li>
        </ol>
        <p className="subtle">
          <strong>If the CRM has no MCP at all:</strong> stay
          hand-authored permanently. Re-author and re-PR the YAML when
          the schema changes. The runtime won&apos;t enable AI
          discovery until an MCP server appears in the deployment.
        </p>
      </details>

      <DiscoverySection />

      <section>
        <div className="toolbar">
          <h2>Advisor signals</h2>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>

        {isLoading ? (
          <p className="empty-state">Loading advisor signals…</p>
        ) : error ? (
          <p className="alert" role="alert">
            Failed to load advisor signals:{" "}
            {error instanceof Error ? error.message : String(error)}
          </p>
        ) : (data?.advisor_signals ?? []).length === 0 ? (
          <p className="empty-state">No CRM advisor signals.</p>
        ) : (
          <SignalTable signals={data!.advisor_signals} />
        )}
      </section>

      {deployments.map((slug) => (
        <DeploymentSection key={slug} deployment={slug} />
      ))}
    </div>
  );
}

// -- Discovery form -------------------------------------------------------

function DiscoverySection() {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const rescan = useMutation({
    mutationFn: (deployment: string) => triggerCrmRescan(deployment),
    onSuccess: (resp) => {
      setErrorMsg(null);
      setResult(
        `Rescan ${resp.rescan_run_id.slice(0, 8)}… queued (${resp.trigger_reason}). New proposal will appear in the table once the worker finishes.`,
      );
      qc.invalidateQueries({ queryKey: ["crm", "advisor-signals"] });
      qc.invalidateQueries({ queryKey: ["crm", "profile"] });
    },
    onError: (err) => {
      setResult(null);
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string } | null;
        if (err.status === 404) {
          setErrorMsg(
            `Deployment "${slug}" is not configured. A first-time deployment needs a hand-authored CrmAdapterProfile YAML committed via PR before rescan works (US6 acceptance scenario 5).`,
          );
        } else {
          setErrorMsg(body?.detail ?? err.message);
        }
      } else if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg(String(err));
      }
    },
  });

  const trimmed = slug.trim();
  return (
    <section>
      <h2>Trigger discovery</h2>
      <p className="empty-state">
        Type the deployment slug and trigger the AI schema advisor over
        MCP. The worker emits a fresh{" "}
        <code>crm_adapter_proposal</code> signal which lands in the
        table below. The endpoint requires the deployment's profile to
        exist already — a brand-new deployment must hand-author a
        starter YAML and merge it via PR first.
      </p>
      <form
        className="form-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) rescan.mutate(trimmed);
        }}
      >
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="deployment-slug"
          aria-label="Deployment slug"
        />
        <button
          type="submit"
          className="btn"
          disabled={!trimmed || rescan.isPending}
        >
          {rescan.isPending ? "Triggering…" : "Trigger discovery"}
        </button>
      </form>
      {result && (
        <p role="status" className="status status--success">
          {result}
        </p>
      )}
      {errorMsg && (
        <p className="alert" role="alert" style={{ marginTop: "var(--space-2)" }}>
          {errorMsg}
        </p>
      )}
    </section>
  );
}

// -- Signals table --------------------------------------------------------

function SignalTable({ signals }: { signals: CrmAdvisorSignal[] }) {
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: (signalId: string) => applyCrmAdvisorSignal(signalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "advisor-signals"] });
      setActionError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string } | null;
        setActionError(body?.detail ?? err.message);
      } else if (err instanceof Error) {
        setActionError(err.message);
      } else {
        setActionError(String(err));
      }
    },
  });

  return (
    <>
      {actionError ? (
        <p className="alert" role="alert">
          {actionError}
        </p>
      ) : null}
      <table className="data-table">
        <thead>
          <tr>
            <th>Deployment</th>
            <th>Source advisor</th>
            <th>Confidence</th>
            <th>Status</th>
            <th>Created</th>
            <th>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {signals.map((s) => (
            <SignalRow
              key={s.id}
              signal={s}
              onApply={() => apply.mutate(s.id)}
              applying={apply.isPending && apply.variables === s.id}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}

function SignalRow({
  signal,
  onApply,
  applying,
}: {
  signal: CrmAdvisorSignal;
  onApply: () => void;
  applying: boolean;
}) {
  const tone = SIGNAL_STATUS_TONE[signal.status] ?? "neutral";
  return (
    <tr>
      <td>
        <code>{signal.deployment_slug}</code>
      </td>
      <td>{signal.source_advisor}</td>
      <td>{(signal.confidence * 100).toFixed(0)}%</td>
      <td>
        <Pill tone={tone}>{signal.status}</Pill>
      </td>
      <td>{new Date(signal.created_at).toLocaleString()}</td>
      <td>
        {signal.status === "open" ? (
          <button
            type="button"
            className="btn"
            onClick={onApply}
            disabled={applying}
          >
            {applying ? "Opening PR…" : "Apply (open PR)"}
          </button>
        ) : signal.resulting_pull_request_ref ? (
          <a
            href={signal.resulting_pull_request_ref}
            target="_blank"
            rel="noreferrer"
          >
            View PR
          </a>
        ) : (
          <span className="empty-state">—</span>
        )}
      </td>
    </tr>
  );
}

// -- Per-deployment profile + rescan -------------------------------------

function DeploymentSection({ deployment }: { deployment: string }) {
  const { data, isLoading, error, refetch } = useQuery(
    crmProfileQueryOptions(deployment),
  );
  const qc = useQueryClient();
  const [rescanError, setRescanError] = useState<string | null>(null);
  const [rescanResult, setRescanResult] = useState<string | null>(null);

  const rescan = useMutation({
    mutationFn: () => triggerCrmRescan(deployment),
    onSuccess: (resp) => {
      setRescanError(null);
      setRescanResult(
        `Rescan ${resp.rescan_run_id.slice(0, 8)}… queued (${resp.trigger_reason})`,
      );
      qc.invalidateQueries({ queryKey: ["crm", "advisor-signals"] });
    },
    onError: (err) => {
      setRescanResult(null);
      if (err instanceof ApiError) {
        const body = err.body as { detail?: string } | null;
        setRescanError(body?.detail ?? err.message);
      } else if (err instanceof Error) {
        setRescanError(err.message);
      } else {
        setRescanError(String(err));
      }
    },
  });

  return (
    <section>
      <div className="toolbar">
        <h2>
          Deployment <code>{deployment}</code>
        </h2>
        <div className="row">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => refetch()}
          >
            Reload profile
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => rescan.mutate()}
            disabled={rescan.isPending}
          >
            {rescan.isPending ? "Triggering…" : "Trigger rescan"}
          </button>
        </div>
      </div>

      {rescanError ? (
        <p className="alert" role="alert">
          {rescanError}
        </p>
      ) : null}
      {rescanResult ? (
        <p className="empty-state">{rescanResult}</p>
      ) : null}

      {isLoading ? (
        <p className="empty-state">Loading profile…</p>
      ) : error instanceof ApiError && error.status === 404 ? (
        <p className="empty-state">
          No active profile for <code>{deployment}</code> yet — apply an
          advisor signal above to open the first PR.
        </p>
      ) : error ? (
        <p className="alert" role="alert">
          Failed to load profile:{" "}
          {error instanceof Error ? error.message : String(error)}
        </p>
      ) : data ? (
        <ProfileView profile={data} />
      ) : null}
    </section>
  );
}

function ProfileView({ profile }: { profile: CrmProfile }) {
  return (
    <table className="data-table">
      <tbody>
        <tr>
          <th>Status</th>
          <td>
            <Pill tone={profile.status === "active" ? "success" : "neutral"}>
              {profile.status}
            </Pill>
          </td>
        </tr>
        <tr>
          <th>Profile path</th>
          <td>
            <code>{profile.profile_path}</code>
          </td>
        </tr>
        <tr>
          <th>Schema fingerprint</th>
          <td>
            <code>{profile.schema_fingerprint}</code>
          </td>
        </tr>
        <tr>
          <th>MCP transcript hash</th>
          <td>
            <code>{profile.mcp_transcript_hash}</code>
          </td>
        </tr>
        <tr>
          <th>Originating signal</th>
          <td>
            <code>{profile.originating_signal_id}</code>
          </td>
        </tr>
        <tr>
          <th>Approved by</th>
          <td>
            <code>{profile.approved_by_actor_id}</code>{" "}
            on {new Date(profile.approved_at).toLocaleString()}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
